'use client';

import { useState, useEffect, useCallback } from 'react';
import { slugify } from '@/lib/slug';

export type RegionType = 'ocean' | 'lake' | 'island' | 'region';

export interface Region {
  id: string;
  name: string;
  name_zh: string;
  name_fr: string;
  name_es: string;
  name_ar: string;
  name_ru: string;
  name_pt: string;
  area_km2: number;
  area_rank: number;
  center: [number, number];
  bbox: [number, number, number, number];
  path: string;
  slug: string;
  color: string; // 用于视觉区分
}

export interface RegionsStats {
  totalRegions: number;
  totalArea: number;
  largestRegion: string;
  smallestRegion: string;
}

// 不同类型区域的颜色配置
const REGION_COLORS = {
  ocean: {
    primary: '#3B82F6',    // 蓝色
    secondary: '#1E40AF',  // 深蓝色
    light: '#DBEAFE',      // 浅蓝色
  },
  lake: {
    primary: '#10B981',    // 绿色
    secondary: '#059669',  // 深绿色
    light: '#D1FAE5',      // 浅绿色
  },
  island: {
    primary: '#F59E0B',    // 橙色
    secondary: '#D97706',  // 深橙色
    light: '#FEF3C7',      // 浅橙色
  },
  region: {
    primary: '#8B5CF6',    // 紫色
    secondary: '#7C3AED',  // 深紫色
    light: '#EDE9FE',      // 浅紫色
  },
};

// 获取区域颜色
const getRegionColor = (type: RegionType, index: number): string => {
  const colors = REGION_COLORS[type];
  const colorVariations = [
    colors.primary,
    colors.secondary,
    '#8B5CF6', // 紫色
    '#EF4444', // 红色
    '#06B6D4', // 青色
    '#84CC16', // 黄绿色
    '#F97316', // 橙红色
    '#EC4899', // 粉色
    '#6366F1', // 靛蓝色
  ];
  return colorVariations[index % colorVariations.length];
};

export function useRegions(type: RegionType) {
  const [regions, setRegions] = useState<Region[]>([]);
  const [stats, setStats] = useState<RegionsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRegions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/data/minis/regions.json');
      if (!response.ok) {
        throw new Error('Failed to load regions data');
      }
      
      const data = await response.json();
      
      // 过滤出指定类型的区域数据
      const regionData = data.filter((region: any) => region.kind === type);
      
      // 处理区域数据
      const processedRegions: Region[] = regionData.map((region: any, index: number) => ({
        id: region.id,
        name: region.name,
        name_zh: region.name_zh || region.name,
        name_fr: region.name_fr || region.name,
        name_es: region.name_es || region.name,
        name_ar: region.name_ar || region.name,
        name_ru: region.name_ru || region.name,
        name_pt: region.name_pt || region.name,
        area_km2: region.area_km2 || 0,
        area_rank: 0, // 稍后计算
        center: region.center || [0, 0],
        bbox: region.bbox || [0, 0, 0, 0],
        path: region.path || `data/regions/${region.id}.json`,
        slug: slugify(region.name),
        color: getRegionColor(type, index)
      }));

      // 按面积排序并设置排名
      processedRegions.sort((a, b) => b.area_km2 - a.area_km2);
      processedRegions.forEach((region, index) => {
        region.area_rank = index + 1;
      });
      
      setRegions(processedRegions);

      // 计算统计信息
      const totalArea = processedRegions.reduce((sum, region) => sum + region.area_km2, 0);
      const largestRegion = processedRegions[0]?.name || '';
      const smallestRegion = processedRegions[processedRegions.length - 1]?.name || '';

      setStats({
        totalRegions: processedRegions.length,
        totalArea: Math.round(totalArea),
        largestRegion,
        smallestRegion
      });

    } catch (err) {
      console.error(`Error loading ${type}s:`, err);
      setError(err instanceof Error ? err.message : `Failed to load ${type}s`);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  // 搜索区域
  const searchRegions = useCallback((query: string) => {
    if (!query.trim()) return regions;
    
    const lowercaseQuery = query.toLowerCase();
    return regions.filter(region => 
      region.name.toLowerCase().includes(lowercaseQuery) ||
      region.name_zh.toLowerCase().includes(lowercaseQuery)
    );
  }, [regions]);

  // 排序
  const sortRegions = useCallback((sortBy: 'area' | 'name', order: 'asc' | 'desc' = 'desc') => {
    const sorted = [...regions];
    
    switch (sortBy) {
      case 'area':
        sorted.sort((a, b) => order === 'desc' ? b.area_km2 - a.area_km2 : a.area_km2 - b.area_km2);
        break;
      case 'name':
        sorted.sort((a, b) => order === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name));
        break;
    }
    
    return sorted;
  }, [regions]);

  return {
    regions,
    stats,
    loading,
    error,
    searchRegions,
    sortRegions,
    refetch: loadRegions,
    type
  };
}
